// Contrato entre o app e o servidor. Duas implementações:
//  - Supabase (produção): supabaseRepository.ts
//  - Memória (só nos testes automáticos): memoryRepository.ts — mesmas regras do SQL.
import type { Subscription, Currency, Plan, Pricing } from '../domain/access';
import type { DateKey } from '../domain/dates';
import type { RecurringFields, RecurringTask } from '../domain/recurring';
import type { Profile, ProfilePatch } from '../domain/settings';
import type { Task, TaskFields } from '../domain/task';
import type { AuthService } from './auth';

export type BackendErrorCode = 'network' | 'no_access' | 'not_found' | 'invalid' | 'unauthenticated' | 'unknown';

export class BackendError extends Error {
  constructor(public code: BackendErrorCode, message?: string) {
    super(message ?? code);
    this.name = 'BackendError';
  }
}

/**
 * O que mudou nas tarefas desde o cursor (sincronização incremental, AC-252): as alteradas, os ids
 * das apagadas e o próximo cursor. `full` = lista inteira (sem cursor ou cursor velho demais).
 */
export interface TaskChanges {
  tasks: Task[];
  deleted: string[];
  cursor: string;
  full: boolean;
}

export interface TaskRepository {
  list(): Promise<Task[]>;
  /** Só o que mudou desde `since` (null = tudo) — o app usa ao voltar o foco. */
  changes(since: string | null): Promise<TaskChanges>;
  create(fields: TaskFields): Promise<Task>;
  update(id: string, fields: Partial<TaskFields>): Promise<Task>;
  remove(id: string): Promise<void>;
  /** Devolve as tarefas que mudaram (a iniciada e a que foi pausada automaticamente). */
  start(id: string): Promise<Task[]>;
  pause(id: string): Promise<Task>;
  finish(id: string): Promise<Task>;
  /** Grava a ordem arrastada de uma seção: cada id recebe (i + 1) / (n + 1) — US-007. */
  reorder(ids: string[]): Promise<void>;
}

/** Tarefas recorrentes (US-095 / US-097) — funções recurring_sync / recurring_clear do banco. */
export interface RecurringRepository {
  list(): Promise<RecurringTask[]>;
  create(fields: RecurringFields): Promise<RecurringTask>;
  update(id: string, fields: RecurringFields): Promise<RecurringTask>;
  remove(id: string): Promise<void>;
  /** Cria as ocorrências que faltam de `today` até 7 dias à frente; devolve só as criadas. */
  sync(today: DateKey): Promise<Task[]>;
  /** Tira da fila as ocorrências não iniciadas de hoje em diante — de uma recorrente ou de todas (id null). */
  clear(id: string | null, today: DateKey): Promise<void>;
}

export interface ProfileRepository {
  get(): Promise<Profile>;
  update(patch: ProfilePatch): Promise<Profile>;
}

export interface SubscriptionRepository {
  get(): Promise<Subscription | null>;
  /**
   * Preços do Stripe na moeda da localização da pessoa (Edge Function prices — AC-110 / AC-111);
   * `deviceCurrency` é a reserva quando não dá para saber o país.
   */
  prices(deviceCurrency: Currency): Promise<Pricing>;
  /** URL da página do Stripe Checkout do plano escolhido (Edge Function create-checkout). */
  checkoutUrl(plan: Plan): Promise<string>;
  /** URL do Portal do Cliente do Stripe (Edge Function customer-portal). */
  portalUrl(): Promise<string>;
}

export interface Backend {
  mode: 'supabase' | 'memory';
  auth: AuthService;
  tasks: TaskRepository;
  recurring: RecurringRepository;
  profile: ProfileRepository;
  subscription: SubscriptionRepository;
  /** Apaga conta, perfil, tarefas e assinatura (Edge Function delete-account). */
  deleteAccount(): Promise<void>;
  /** O servidor responde? (detecção de offline — ASM-070) */
  ping(): Promise<boolean>;
  /** Hora do servidor (ms) — corrige a diferença do relógio do aparelho na exibição. */
  serverNow(): Promise<number>;
}
